import React from 'react';
import {FormField, FormSection, FormActions} from '../../../../Presentation/components/common';
import {useFormState, CommonValidationRules, createFieldProps} from '../../../../Presentation/utils/formUtils';
import Card from '../../../../Presentation/components/ui/Card';
import {AppText, AppTitle, AppSmallText} from '../../../../Presentation/components/ui/Typography';
import Button from '../../../../Presentation/components/ui/Button';
import {
  Permission,
  CreatePermissionRequest,
  UpdatePermissionRequest,
  ResourceType,
  ActionType
} from '../../models/permission.model';

interface PermissionFormProps {
  permission?: Permission;
  onSubmit: (permissionData: CreatePermissionRequest | UpdatePermissionRequest) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const RESOURCE_OPTIONS = Object.values(ResourceType).map(resource => ({
  value: resource,
  label: getResourceDisplayName(resource),
  description: `إدارة ${getResourceDisplayName(resource)}`
}));

const ACTION_OPTIONS = Object.values(ActionType).map(action => ({
  value: action,
  label: getActionDisplayName(action),
  description: getActionDescription(action)
}));

function getResourceDisplayName (resource: string): string {
  const resourceNames: Record<string, string> = {
    [ResourceType.USERS]: 'المستخدمين',
    [ResourceType.STORES]: 'المتاجر',
    [ResourceType.PRODUCTS]: 'المنتجات',
    [ResourceType.INVENTORY]: 'المخزون',
    [ResourceType.SALES]: 'المبيعات',
    [ResourceType.DISTRIBUTION]: 'التوزيع',
    [ResourceType.VISITS]: 'الزيارات',
    [ResourceType.BILLING]: 'الفواتير',
    [ResourceType.REPORTS]: 'التقارير',
    [ResourceType.SETTINGS]: 'الإعدادات',
    [ResourceType.PERMISSIONS]: 'الصلاحيات',
    [ResourceType.ROLES]: 'الأدوار',
    [ResourceType.BRANDS]: 'البراندات',
    [ResourceType.DASHBOARD]: 'لوحة التحكم',
    [ResourceType.MOBILE]: 'التطبيق المحمول',
    [ResourceType.SYSTEM]: 'النظام'
  };
  return resourceNames[resource] || resource;
}

function getActionDisplayName (action: string): string {
  const actionNames: Record<string, string> = {
    [ActionType.CREATE]: 'إنشاء',
    [ActionType.READ]: 'عرض',
    [ActionType.UPDATE]: 'تعديل',
    [ActionType.DELETE]: 'حذف',
    [ActionType.LIST]: 'قائمة',
    [ActionType.EXPORT]: 'تصدير',
    [ActionType.IMPORT]: 'استيراد',
    [ActionType.APPROVE]: 'موافقة',
    [ActionType.REJECT]: 'رفض',
    [ActionType.ASSIGN]: 'تعيين',
    [ActionType.MANAGE]: 'إدارة'
  };
  return actionNames[action] || action;
}

function getActionDescription (action: string): string {
  const descriptions: Record<string, string> = {
    [ActionType.CREATE]: 'إنشاء عنصر جديد',
    [ActionType.READ]: 'عرض وعرض التفاصيل',
    [ActionType.UPDATE]: 'تعديل العناصر الموجودة',
    [ActionType.DELETE]: 'حذف العناصر',
    [ActionType.LIST]: 'عرض قائمة العناصر',
    [ActionType.EXPORT]: 'تصدير البيانات',
    [ActionType.IMPORT]: 'استيراد البيانات',
    [ActionType.APPROVE]: 'الموافقة على العناصر',
    [ActionType.REJECT]: 'رفض العناصر',
    [ActionType.ASSIGN]: 'تعيين العناصر',
    [ActionType.MANAGE]: 'إدارة شاملة للعناصر'
  };
  return descriptions[action] || '';
}

export const PermissionForm: React.FC<PermissionFormProps> = ({
  permission,
  onSubmit,
  onCancel,
  loading = false
}) => {
  const isEditMode = !!permission;

  // Centralized form state
  const [formState, formActions] = useFormState(
    {
      name: permission?.name || '',
      resource: permission?.resource || '',
      action: permission?.action || '',
      description: permission?.description || ''
    },
    {
      name: {
        required: true,
        customValidator: (value: string) => {
          if (!value?.trim()) {
            return 'اسم الصلاحية مطلوب';
          }
          if (!/^[a-z][a-z0-9_]*$/.test(value)) {
            return 'اسم الصلاحية يجب أن يبدأ بحرف صغير ويحتوي على أحرف صغيرة وأرقام وشرطات سفلية فقط';
          }
          return null;
        }
      },
      resource: CommonValidationRules.required('المورد'),
      action: CommonValidationRules.required('الإجراء'),
      description: {
        customValidator: (value: string) => {
          if (value && value.length > 500) {
            return 'الوصف لا يمكن أن يتجاوز 500 حرف';
          }
          return null;
        }
      }
    }
  );

  const handleSubmit = async (data: any) => {
    try {
      const permissionData = {
        name: data.name,
        resource: data.resource,
        action: data.action,
        description: data.description || undefined
      };

      if (permission) {
        // Update existing permission
        await onSubmit({
          id: permission.id,
          description: permissionData.description
        });
      } else {
        // Create new permission
        await onSubmit(permissionData);
      }
    } catch (error) {
      // Error handling is done in the parent component
    }
  };

  const generatePermissionName = () => {
    if (formState.data.resource && formState.data.action) {
      const name = `${formState.data.resource}_${formState.data.action}`;
      formActions.setField('name', name);
    }
  };

  return (
      <form onSubmit={(e) => { e.preventDefault(); formActions.submit(handleSubmit); }} className="space-y-4">
        <FormSection title="معلومات الصلاحية">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              type="select"
              label="المورد *"
              options={RESOURCE_OPTIONS}
              {...createFieldProps('resource', formState, formActions)}
            />

            <FormField
              type="select"
              label="الإجراء *"
              options={ACTION_OPTIONS}
              {...createFieldProps('action', formState, formActions)}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                اسم الصلاحية *
              </label>
              <div className="flex space-x-2">
                <div className="flex-1">
                  <FormField
                    type="text"
                    placeholder="user_create"
                    {...createFieldProps('name', formState, formActions)}
                  />
                </div>
                {!isEditMode && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={generatePermissionName}
                    disabled={!formState.data.resource || !formState.data.action}
                    className="px-3"
                  >
                    توليد
                  </Button>
                )}
              </div>
              <AppSmallText className="text-gray-500 mt-1">
                يجب أن يبدأ بحرف صغير ويحتوي على أحرف صغيرة وأرقام وشرطات سفلية فقط
              </AppSmallText>
            </div>

            <FormField
              type="text"
              label="الوصف"
              placeholder="وصف الصلاحية"
              {...createFieldProps('description', formState, formActions)}
            />
          </div>
        </FormSection>

        <FormActions
          onSubmit={() => formActions.submit(handleSubmit)}
          onCancel={onCancel}
          submitLabel={isEditMode ? 'حفظ التغييرات' : 'إنشاء صلاحية'}
          cancelLabel="إلغاء"
          isLoading={loading || formState.isSubmitting}
          disabled={!formState.isValid}
          showReset={false}
        />
      </form>
  );
};
